import { faUserShield } from "@fortawesome/free-solid-svg-icons";
import React from "react";
import LegalPageLayout from "../Components/LegalPageLayout";

function PrivacyMS() {
  return (
    <LegalPageLayout
      titleId="privacy-ms-title"
      icon={faUserShield}
      title="Privacy Policy (MileSoft Sparq)"
      description={
        <p>
          Your privacy is our priority. This page explains how MileSoft Sparq
          handles information.
        </p>
      }
    >
      <p className="effective-date">
        <strong>Effective Date:</strong> October 8, 2026
      </p>

      <p>
        MileSoft Sparq is a community-powered electricity outage reporting
        application developed and operated by MileSoft. It allows users to
        report electricity blackouts, report when electricity is restored,
        and view electricity availability information shared by other
        users across different locations.
      </p>

      <p>
        MileSoft is the data controller for personal information processed
        through MileSoft Sparq. For privacy requests, data protection
        officer (DPO) requests, and controller contact purposes, email us
        at <strong><a href="mailto:privacy@milesoft.africa">privacy@milesoft.africa</a></strong>.
      </p>

      <h5>Information We Collect</h5>

      <p>
        MileSoft Sparq does not collect personal details such as your name,
        email address, or profile information.
      </p>

      <p>MileSoft Sparq may collect or process the following information:</p>

      <ul>
        <li>Device location coordinates when you grant location permission</li>
        <li>
          Geographical locations manually selected when submitting reports
        </li>
        <li>Electricity blackout and restoration reports</li>
        <li>Report timestamps, status, and reporting history</li>
        <li>Report corrections, withdrawals, and submission status</li>
        <li>
          Information necessary to consolidate multiple reports within the
          same geographical area
        </li>
        <li>
          Device ID, device type, operating system, app version, app
          activity, crash logs, diagnostics, error logs, and analytics
          where enabled
        </li>
      </ul>

      <p>
        Location information may be sensitive because it can reveal places
        associated with your activities, home, or workplace.
      </p>

      <p>
        We aim to limit the collection and processing of personal
        information to what is reasonably necessary for the application's
        functionality.
      </p>

      <h5>How We Use Information and Legal Basis</h5>

      <p>We use your information to:</p>

      <ul>
        <li>Allow you to report electricity blackouts</li>
        <li>Allow you to report electricity restoration</li>
        <li>
          Display community-reported electricity conditions on a shared
          map
        </li>
        <li>
          Identify geographical areas affected by reported electricity
          outages
        </li>
        <li>Consolidate multiple reports from the same geographical area</li>
        <li>
          Display when reports were submitted and identify outdated
          information
        </li>
        <li>Allow users to correct or withdraw their own recent reports</li>
        <li>
          Synchronize pending reports when internet connectivity becomes
          available
        </li>
        <li>Provide technical support</li>
        <li>Improve application security, reliability, and performance</li>
      </ul>

      <p>
        Our legal basis for processing may include performance of a
        contract or requested service, consent, legitimate interests in
        operating and securing the application, compliance with legal
        obligations, and processing requested by you.
      </p>

      <p>
        Device location access is permission-based. You may decline
        location permission and manually select a location when submitting
        a report.
      </p>

      <h5>Storage and Service Providers</h5>

      <p>
        MileSoft Sparq may use cloud-based services for data storage,
        synchronization, mapping, diagnostics, and
        application performance monitoring.
      </p>

      <p>
        Information may also be temporarily stored on your device to
        support reporting when internet connectivity is unavailable.
      </p>

      <p>
        Pending reports may be synchronized with cloud services when
        connectivity is restored.
      </p>

      <p>
        Your information may be processed or stored in countries other
        than your own, where data protection laws may differ from those in
        your country.
      </p>

      <p>
        We take reasonable measures to ensure that service providers
        process information securely and only for authorized purposes.
      </p>

      <h5>Information Sharing, Sales, and Advertising</h5>

      <p>
        MileSoft does not sell your personal information or use your
        location information for targeted advertising.
      </p>

      <p>
        MileSoft Sparq is a community reporting application. When you
        submit an electricity blackout or restoration report, certain
        information is intentionally shared with other application users.
      </p>

      <p>Publicly visible information may include:</p>

      <ul>
        <li>The geographical area associated with a report</li>
        <li>Whether electricity is reported as unavailable or restored</li>
        <li>The time or relative age of the report</li>
        <li>Consolidated reporting information for the affected area</li>
      </ul>

      <p>
        <strong>MileSoft Sparq does not collect or display your name, email
        address, or profile information alongside publicly shared electricity
        reports.</strong>
      </p>

      <p>
        Reports may be grouped or geographically generalized to reduce
        unnecessary exposure of precise personal locations.
      </p>

      <p>
        We may share limited information with technology service providers
        where necessary to operate MileSoft Sparq. We may also disclose
        information when required by law, to respond to lawful requests,
        to protect users, MileSoft, or the public, or during a business
        transfer.
      </p>

      <p>
        You may opt out of any sale, sharing for targeted advertising, or
        other processing subject to applicable opt-out rights by emailing{" "}
        <strong><a href="mailto:privacy@milesoft.africa">privacy@milesoft.africa</a></strong>. We will honor applicable rights even
        though we do not sell personal information or use it for targeted
        advertising.
      </p>

      <h5>Data Security</h5>

      <p>
        We use reasonable technical and organizational safeguards to
        protect information processed through MileSoft Sparq.
      </p>

      <p>
        These safeguards may include encrypted connections,
        database access restrictions, cloud security
        protections, and validation of submitted reports.
      </p>

      <p>
        We also aim to prevent unauthorized modification or withdrawal of
        reports submitted by other users.
      </p>

      <p>
        Public reporting is designed to reduce unnecessary exposure of
        precise personal locations.
      </p>

      <p>
        No electronic system can guarantee complete security. You are
        responsible for protecting your device.
      </p>

      <h5>Data Retention and Deletion</h5>

      <p>
        We retain electricity blackout and restoration reports for as long
        as reasonably necessary to provide community reporting, identify
        outdated information, maintain reporting accuracy, and support
        application operations.
      </p>

      <p>
        Older reports may be marked as stale, removed from active outage
        views, or retained as historical records where appropriate.
      </p>

      <p>
        You may correct or withdraw your own recent reports using the
        functionality provided in the application.
      </p>

      <p>
        You may request deletion of information associated with your reports
        by emailing{" "} <strong><a href="mailto:privacy@milesoft.africa">privacy@milesoft.africa</a></strong> or
        through our <a href="https://milesoft.africa/contacts">contact page</a>.
      </p>

      <p>
        Please identify the reports concerned so we can assess your request.
      </p>

      <p>
        Verified deletion requests will normally be processed within 30
        days, unless limited retention is required or permitted for legal
        compliance, security, fraud prevention, dispute resolution, or
        backups.
      </p>

      <p>
        Some report information that has been aggregated or anonymized so
        that it no longer identifies an individual may be retained for
        statistical or historical purposes.
      </p>

      <p>
        Deleting the application from your device does not automatically
        delete information previously submitted to our services.
      </p>

      <h5>Your Privacy Rights</h5>

      <p>
        Depending on where you live and applicable law, you may have the
        right to request access to your personal information, correct
        inaccurate information, request deletion, receive a portable copy,
        object to or restrict certain processing, withdraw consent where
        processing is based on consent, and know whether your information
        is sold or shared.
      </p>

      <p>
        You may manage device location permissions through your device
        settings.
      </p>

      <p>
        Declining location permission does not prevent you from viewing
        community reports or manually selecting a reporting location.
      </p>

      <p>
        To make a privacy rights request, contact{" "}
        <strong><a href="mailto:privacy@milesoft.africa">privacy@milesoft.africa</a></strong>.
      </p>

      <h5>Children's Privacy</h5>

      <p>MileSoft Sparq is not designed specifically for children under 13.</p>

      <p>A parent or guardian should supervise use by a child.</p>

      <p>
        If we learn that a child has provided personal information without
        appropriate consent, we will take reasonable steps to delete it.
      </p>

      <h5>Electricity Reporting Disclaimer</h5>

      <p>
        MileSoft Sparq is a community-powered electricity outage
        information platform. It is not an official electricity utility
        monitoring or emergency response service.
      </p>

      <p>
        Electricity blackout and restoration information is submitted by
        users and may not be independently verified.
      </p>

      <p>
        Reports may be inaccurate, incomplete, delayed, or outdated. An
        absence of reported blackouts does not necessarily mean
        electricity is available in a particular area.
      </p>

      <p>
        Users should confirm critical electricity supply information with
        their electricity service provider or other appropriate
        authorities.
      </p>

      <p>
        MileSoft Sparq does not guarantee uninterrupted electricity
        supply, the accuracy of community reports, or the availability of
        its reporting services.
      </p>

      <p>
        Do not rely exclusively on MileSoft Sparq for emergencies,
        electrical safety decisions, or activities where inaccurate
        electricity availability information could cause harm.
      </p>

      <h5>Changes to This Privacy Policy</h5>

      <p>
        MileSoft may update this Privacy Policy when the application, our
        services, or legal requirements change.
      </p>

      <p>
        When we update it, we will revise the effective date and may
        notify users through the application or another reasonable
        method.
      </p>

      <h5>Contact Us</h5>

      <p>
        <strong>Company and App Provider:</strong> MileSoft
        <br />
        <strong>App:</strong> MileSoft Sparq
        <br />
        <strong>Controller and DPO Contact: {" "}</strong><a href="mailto:privacy@milesoft.africa">privacy@milesoft.africa</a>
        <br />
        <strong>Location:</strong> Malawi
        <br />
        <strong>Website:</strong> <a href="https://milesoft.africa/">milesoft.africa</a>
      </p>
    </LegalPageLayout>
  );
}

export default PrivacyMS;
